import {
  Container,
  Stack,
  Box,
  Text,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import CountUp from "react-countup";
import SectionHeading from "./SectionHeading";
import t from "../translations";

const MotionBox = motion(Box);

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

function StatCard({ stat, color }) {
  const cardBg = useColorModeValue("gray.50", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <Box
      ref={ref}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      p={{ base: 4, md: 5 }}
      textAlign="center"
    >
      <Text
        fontWeight={800}
        fontSize={{ base: "2xl", md: "3xl" }}
        color={`${color}.400`}
      >
        {inView ? (
          <CountUp end={stat.value} duration={2} suffix={stat.suffix || ""} />
        ) : (
          "0"
        )}
      </Text>
      <Text fontSize="sm" color="gray.500" mt={1}>
        {stat.label}
      </Text>
    </Box>
  );
}

export default function About({ color = "purple" }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });
  const textColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Container maxW="4xl" id="about" py={{ base: 16, md: 24 }}>
      <MotionBox
        ref={ref}
        variants={stagger}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Stack spacing={{ base: 6, md: 10 }}>
          <MotionBox variants={fadeUp}>
            <SectionHeading>{t.about.heading}</SectionHeading>
          </MotionBox>

          <MotionBox variants={fadeUp} px={4}>
            <Text color={textColor} fontSize="lg" lineHeight={1.8}>
              {t.about.description}
            </Text>
          </MotionBox>

          <MotionBox variants={fadeUp} px={4}>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              {t.about.stats.map((stat) => (
                <StatCard key={stat.label} stat={stat} color={color} />
              ))}
            </SimpleGrid>
          </MotionBox>
        </Stack>
      </MotionBox>
    </Container>
  );
}
